import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import {
  Gift, Ticket, Plus, Copy, Trash2, Power, Loader2, Users, Coins,
  Megaphone, Sparkles, Link2, Check, Infinity as InfinityIcon, Clock,
  Percent, Eye, Image as ImageIcon,
} from "lucide-react";
import { PromoRedemptionsDialog } from "@/pages/admin/PromoRedemptionsDialog";
import { PromoBannerDialog } from "@/pages/admin/PromoBannerDialog";

const CREDIT_PRESETS = [5, 8, 10, 25];
const DISCOUNT_PRESETS = [10, 15, 20, 30];
const field = "w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none";

const STATUS_META = {
  active: { label: "Actif", cls: "text-signal-success border-signal-success/40 bg-signal-success/10" },
  disabled: { label: "Désactivé", cls: "text-zinc-400 border-white/15 bg-white/5" },
  expired: { label: "Expiré", cls: "text-signal-error border-signal-error/40 bg-signal-error/10" },
  exhausted: { label: "Épuisé", cls: "text-amber-300 border-amber-400/40 bg-amber-400/10" },
};

const StatCard = ({ icon: Icon, value, label, color }) => (
  <div className="glass rounded-md p-5 flex items-center gap-4">
    <div className="w-11 h-11 grid place-items-center rounded-sm border shrink-0" style={{ color, borderColor: `${color}55`, background: `${color}12` }}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <div className="font-display text-2xl font-bold leading-none">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{label}</div>
    </div>
  </div>
);

export const AdminPromo = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, redemptions: 0, credits_distributed: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState("");
  const [redeemFor, setRedeemFor] = useState(null);
  const [bannerFor, setBannerFor] = useState(null);
  const [form, setForm] = useState({ reward_type: "credits", campaign: "", label: "", credits: 8, discount_pct: 20, max_uses: 25, unlimited: false, code: "", expires_at: "" });

  const load = () => api.get("/admin/promo-codes").then(({ data }) => {
    setCodes(data.codes);
    setStats(data.stats);
  }).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((o) => ({ ...o, [k]: v }));

  const create = async () => {
    if (form.reward_type === "credits" && (!form.credits || form.credits <= 0)) { toast.error("Indiquez le nombre de crédits offerts."); return; }
    if (form.reward_type === "discount" && (!form.discount_pct || form.discount_pct <= 0)) { toast.error("Indiquez le pourcentage de réduction."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/promo-codes", {
        reward_type: form.reward_type,
        campaign: form.campaign,
        label: form.label,
        credits: Number(form.credits),
        discount_pct: Number(form.discount_pct),
        max_uses: form.unlimited ? 0 : Number(form.max_uses),
        code: form.code,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : "",
      });
      toast.success(`Code ${data.code} généré 🎉`);
      setForm({ reward_type: form.reward_type, campaign: "", label: "", credits: 8, discount_pct: 20, max_uses: 25, unlimited: false, code: "", expires_at: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Création impossible");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (c) => {
    try {
      await api.patch(`/admin/promo-codes/${c.id}`, { active: !c.active });
      load();
    } catch (e) { toast.error("Action impossible"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer le code ${c.code} ?`)) return;
    try {
      await api.delete(`/admin/promo-codes/${c.id}`);
      toast.success("Code supprimé");
      load();
    } catch (e) { toast.error("Suppression impossible"); }
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success("Copié dans le presse-papiers");
    setTimeout(() => setCopied(""), 1500);
  };

  const shareLink = (code) => `${window.location.origin}/client/credits?promo=${code}`;

  return (
    <AppShell mode="admin">
      <div className="mb-8">
        <div className="font-mono text-xs text-aurora-purple tracking-[0.3em] uppercase mb-2 flex items-center gap-2">
          <Megaphone className="w-3.5 h-3.5" /> // Acquisition & Forums
        </div>
        <h1 className="font-display text-4xl font-bold uppercase tracking-tight">Codes Promo & Giveaways</h1>
        <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
          Générez des codes à partager sur les forums. Chaque code offre des crédits — utilisables sur n'importe quelle solution ou fichier. Montrez que vous êtes actif et attirez de nouveaux clients.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Ticket} value={stats.total} label="Codes créés" color="#00E5FF" />
        <StatCard icon={Power} value={stats.active} label="Codes actifs" color="#00E5A0" />
        <StatCard icon={Users} value={stats.redemptions} label="Utilisations" color="#8B5CF6" />
        <StatCard icon={Coins} value={stats.credits_distributed} label="Crédits offerts" color="#FFB020" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Create panel */}
        <div className="lg:col-span-2">
          <div className="glass rounded-md p-6 sticky top-4">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-aurora-cyan" /> Nouveau code
            </h3>
            <div className="space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Type de récompense</label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button type="button" data-testid="promo-type-credits" onClick={() => set("reward_type", "credits")}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-sm border font-display uppercase tracking-wide text-xs transition-colors ${form.reward_type === "credits" ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/15 text-zinc-400 hover:border-white/30"}`}>
                    <Coins className="w-4 h-4" /> Crédits
                  </button>
                  <button type="button" data-testid="promo-type-discount" onClick={() => set("reward_type", "discount")}
                    className={`flex items-center justify-center gap-2 py-2.5 rounded-sm border font-display uppercase tracking-wide text-xs transition-colors ${form.reward_type === "discount" ? "border-aurora-purple text-aurora-purple bg-aurora-purple/10" : "border-white/15 text-zinc-400 hover:border-white/30"}`}>
                    <Percent className="w-4 h-4" /> Réduction
                  </button>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Campagne (interne)</label>
                <input data-testid="promo-campaign" className={`${field} mt-1.5`} placeholder="Forum BMW-Tuning · Juin 2026" value={form.campaign} onChange={(e) => set("campaign", e.target.value)} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Libellé public (optionnel)</label>
                <input data-testid="promo-label" className={`${field} mt-1.5`} placeholder="Ex : 1 Stage 1 offert" value={form.label} onChange={(e) => set("label", e.target.value)} />
              </div>
              {form.reward_type === "credits" ? (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Crédits offerts</label>
                  <input data-testid="promo-credits" type="number" min="1" className={`${field} mt-1.5`} value={form.credits} onChange={(e) => set("credits", e.target.value)} />
                  <div className="flex gap-2 mt-2">
                    {CREDIT_PRESETS.map((n) => (
                      <button key={n} type="button" onClick={() => set("credits", n)}
                        className={`px-2.5 py-1 rounded-sm font-mono text-[11px] border transition-colors ${Number(form.credits) === n ? "border-aurora-cyan text-aurora-cyan bg-aurora-cyan/10" : "border-white/15 text-zinc-400 hover:border-white/30"}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Réduction sur l'achat de crédits (%)</label>
                  <input data-testid="promo-discount" type="number" min="1" max="90" className={`${field} mt-1.5`} value={form.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} />
                  <div className="flex gap-2 mt-2">
                    {DISCOUNT_PRESETS.map((n) => (
                      <button key={n} type="button" onClick={() => set("discount_pct", n)}
                        className={`px-2.5 py-1 rounded-sm font-mono text-[11px] border transition-colors ${Number(form.discount_pct) === n ? "border-aurora-purple text-aurora-purple bg-aurora-purple/10" : "border-white/15 text-zinc-400 hover:border-white/30"}`}>
                        {n}%
                      </button>
                    ))}
                  </div>
                  <p className="font-mono text-[10px] text-zinc-500 mt-2">Appliquée automatiquement au prochain achat de crédits du client.</p>
                </div>
              )}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Nombre d'utilisations</label>
                <div className="flex items-center gap-2 mt-1.5">
                  <input data-testid="promo-max-uses" type="number" min="1" disabled={form.unlimited} className={`${field} flex-1 ${form.unlimited ? "opacity-40" : ""}`} value={form.max_uses} onChange={(e) => set("max_uses", e.target.value)} />
                  <button type="button" data-testid="promo-unlimited"
                    onClick={() => set("unlimited", !form.unlimited)}
                    className={`flex items-center gap-1.5 px-3 py-2.5 rounded-sm font-mono text-[11px] uppercase tracking-widest border transition-colors whitespace-nowrap ${form.unlimited ? "border-aurora-purple text-aurora-purple bg-aurora-purple/10" : "border-white/15 text-zinc-400 hover:border-white/30"}`}>
                    <InfinityIcon className="w-3.5 h-3.5" /> Illimité
                  </button>
                </div>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Expiration (optionnel)</label>
                <input data-testid="promo-expires" type="date" className={`${field} mt-1.5`} value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Code personnalisé (optionnel)</label>
                <input data-testid="promo-custom-code" className={`${field} mt-1.5 font-mono uppercase`} placeholder="Auto-généré si vide" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} />
              </div>
              <button data-testid="promo-create-btn" onClick={create} disabled={creating}
                className="w-full flex items-center justify-center gap-2 py-3 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider rounded-sm hover:bg-cyan-300 transition-colors disabled:opacity-60">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Générer le code
              </button>
            </div>
          </div>
        </div>

        {/* Codes list */}
        <div className="lg:col-span-3 space-y-3">
          {loading ? (
            <div className="glass rounded-md py-16 text-center font-mono text-xs text-zinc-500">Chargement...</div>
          ) : codes.length === 0 ? (
            <div className="glass rounded-md py-16 text-center" data-testid="promo-empty">
              <Gift className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Aucun code promo. Créez votre premier giveaway pour les forums.</p>
            </div>
          ) : (
            codes.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.active;
              const pct = c.max_uses ? Math.min(100, Math.round((c.uses / c.max_uses) * 100)) : 0;
              return (
                <div key={c.id} data-testid={`promo-row-${c.code}`} className="glass rounded-md p-5">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <button onClick={() => copy(c.code, `code-${c.id}`)} data-testid={`promo-copy-${c.code}`}
                          className="group flex items-center gap-2 font-mono text-lg font-bold text-white hover:text-aurora-cyan transition-colors">
                          {c.code}
                          {copied === `code-${c.id}` ? <Check className="w-4 h-4 text-signal-success" /> : <Copy className="w-4 h-4 opacity-40 group-hover:opacity-100" />}
                        </button>
                        <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${meta.cls}`}>{meta.label}</span>
                      </div>
                      <div className="font-mono text-[11px] text-zinc-500 mt-1">
                        {c.campaign || "—"}{c.label ? ` · ${c.label}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {c.reward_type === "discount" ? (
                        <div className="flex items-center gap-1.5 text-aurora-purple justify-end">
                          <Percent className="w-4 h-4" />
                          <span className="font-mono text-lg font-bold">-{c.discount_pct}%</span>
                          <span className="font-mono text-[10px] text-zinc-500">crédits</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-aurora-cyan justify-end">
                          <Coins className="w-4 h-4" />
                          <span className="font-mono text-lg font-bold">{c.credits}</span>
                          <span className="font-mono text-[10px] text-zinc-500">crédits</span>
                        </div>
                      )}
                      {c.expires_at && (
                        <div className="font-mono text-[10px] text-zinc-500 mt-1 flex items-center gap-1 justify-end">
                          <Clock className="w-3 h-3" /> {new Date(c.expires_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex items-center justify-between font-mono text-[10px] text-zinc-500 mb-1">
                        <span>Utilisations</span>
                        <span>{c.uses}{c.max_uses ? ` / ${c.max_uses}` : " (illimité)"}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full bg-aurora-cyan transition-all" style={{ width: c.max_uses ? `${pct}%` : "100%", opacity: c.max_uses ? 1 : 0.3 }} />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <button onClick={() => setRedeemFor(c)} data-testid={`promo-uses-${c.code}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest border border-white/15 text-zinc-300 hover:border-aurora-purple/50 hover:text-aurora-purple transition-colors">
                      <Eye className="w-3.5 h-3.5" /> Utilisations
                    </button>
                    <button onClick={() => setBannerFor(c)} data-testid={`promo-banner-${c.code}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest border border-white/15 text-zinc-300 hover:border-aurora-cyan/50 hover:text-aurora-cyan transition-colors">
                      <ImageIcon className="w-3.5 h-3.5" /> Bannière
                    </button>
                    <button onClick={() => copy(shareLink(c.code), `link-${c.id}`)} data-testid={`promo-sharelink-${c.code}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest border border-white/15 text-zinc-300 hover:border-aurora-cyan/50 hover:text-aurora-cyan transition-colors">
                      {copied === `link-${c.id}` ? <Check className="w-3.5 h-3.5 text-signal-success" /> : <Link2 className="w-3.5 h-3.5" />}
                      Lien
                    </button>
                    <button onClick={() => toggle(c)} data-testid={`promo-toggle-${c.code}`}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest border transition-colors ${c.active ? "border-white/15 text-zinc-300 hover:border-amber-400/50 hover:text-amber-300" : "border-signal-success/40 text-signal-success bg-signal-success/10"}`}>
                      <Power className="w-3.5 h-3.5" /> {c.active ? "Désactiver" : "Activer"}
                    </button>
                    <button onClick={() => remove(c)} data-testid={`promo-delete-${c.code}`}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-sm font-mono text-[11px] uppercase tracking-widest border border-white/15 text-zinc-400 hover:border-signal-error/50 hover:text-signal-error transition-colors ml-auto">
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <PromoRedemptionsDialog code={redeemFor} onClose={() => setRedeemFor(null)} />
      <PromoBannerDialog code={bannerFor} onClose={() => setBannerFor(null)} />
    </AppShell>
  );
};
